import * as React from "react";
import { cn } from "../../lib/utils";

export interface ViewOption<T extends string> {
  value: T;
  label: string;
  icon: React.ReactNode;
}

export interface ViewSwitcherProps<T extends string> {
  options: ViewOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  className?: string;
}

/**
 * ViewSwitcher Component
 *
 * A compact toggle group for switching between different view modes (e.g., list, grid, table, board).
 *
 * Features:
 * - Icon-based view options
 * - Selected state with background highlight
 * - Rounded container with grouped buttons
 * - Keyboard navigation support
 *
 * @example
 * ```tsx
 * import { ViewSwitcher } from "@polaris/ui";
 * import { List, LayoutGrid } from "lucide-react";
 *
 * const options = [
 *   { value: "table", label: "Table view", icon: <List className="h-4 w-4" /> },
 *   { value: "grid", label: "Grid view", icon: <LayoutGrid className="h-4 w-4" /> },
 * ];
 *
 * <ViewSwitcher
 *   options={options}
 *   value={viewMode}
 *   onValueChange={setViewMode}
 * />
 * ```
 */
export function ViewSwitcher<T extends string>({
  options,
  value,
  onValueChange,
  className,
}: ViewSwitcherProps<T>) {
  return (
    <div
      className={cn(
        "inline-flex h-control shrink-0 items-center gap-0.5 rounded-md bg-hover p-0.5",
        className,
      )}
      role="tablist"
      aria-label="View options"
    >
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isSelected}
            aria-label={option.label}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "inline-grid size-6 shrink-0 place-items-center rounded-sm",
              "transition-[background-color,color] duration-100 ease-out-fast",
              "focus-visible:outline-none focus-fill",
              "disabled:pointer-events-none disabled:opacity-50",
              "[&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
              isSelected
                ? "bg-elevated text-text"
                : "text-text-tertiary hover:bg-hover-strong hover:text-text",
            )}
          >
            {option.icon}
          </button>
        );
      })}
    </div>
  );
}

ViewSwitcher.displayName = "ViewSwitcher";
